import {
	HttpClient,
	HttpResponse
}                             from '@angular/common/http';
import { Injectable }         from '@angular/core';
import { BusinessPremises }   from '@app/modules/business-premises/models/business-premises.model';
import {
	ElsterConfig,
	SchedulingConfig,
	SystemConfig
}                             from '@app/modules/configuration/models/configuration.model';
import {
	IBusinessPremisesHttpParams,
	RequestActionEnum,
	RequestCompleteEntityState,
	RequestCompleteListState
}                             from '@core/interfaces/api.interface';
import { AbstractApiService } from '@http/abstract-api.service';
import { MapperService }      from '@http/mapper.service';
import { SanitizerService }   from '@http/sanitizer.service';
import { TransitionService }  from '@http/transition.service';
import { IHttpContextConfig } from '@interceptors/interceptors.model';
import { ModelTypeEnum }      from '@models/transition.model';
import {
	AdminBusinessPremisesApiClient,
	ConfigTypeDTO,
	CreateBusinessPremisesElsterConfigRequestParams as AdminCreateBusinessPremisesElsterConfigRequestParams,
	CreateBusinessPremisesSystemConfigRequestParams as AdminCreateBusinessPremisesSystemConfigRequestParams,
	DeleteBusinessPremisesElsterConfigRequestParams as AdminDeleteBusinessPremisesElsterConfigRequestParams,
	DeleteBusinessPremisesSystemConfigRequestParams,
	ElsterConfigurationDTO,
	GetBusinessPremisesElsterConfigRequestParams as AdminGetBusinessPremisesElsterConfigRequestParams,
	UpdateBusinessPremisesElsterConfigRequestParams as AdminUpdateBusinessPremisesElsterConfigRequestParams,
	UpdateBusinessPremisesSystemConfigRequestParams as AdminUpdateBusinessPremisesSystemConfigRequestParams
}                             from '@openApiGenerated/admin-api';
import {
	AddressDataDTO,
	BusinessPremisesApiClient,
	BusinessPremisesBodyDTO,
	BusinessPremisesDTO,
	BusinessPremisesStatusDTO,
	BusinessPremisesStatusEnumDTO,
	BusinessPremisesUpdateBodyDTO,
	ClsBusinessPremisesBodyDTO,
	ClsBusinessPremisesStatusDTO,
	ClsBusinessPremisesUpdateBodyDTO,
	ClsSchedulingConfigurationDTO,
	ClsSystemConfigurationBodyDTO,
	CreateBusinessPremisesElsterConfigRequestParams,
	CreateBusinessPremisesForOrganizationRequestParams,
	CreateBusinessPremisesSchedulingConfigRequestParams,
	CreateBusinessPremisesSystemConfigRequestParams,
	DeleteBusinessPremisesElsterConfigRequestParams,
	DeleteBusinessPremisesRequestParams,
	DeleteBusinessPremisesSchedulingConfigRequestParams,
	GetBusinessPremisesByCriteriaRequestParams,
	GetBusinessPremisesElsterConfigRequestParams,
	GetBusinessPremisesRequestParams,
	GetBusinessPremisesSchedulingConfigRequestParams,
	GetBusinessPremisesSystemConfigRequestParams,
	InvalidEntitiesDTO,
	SchedulingConfigurationDTO,
	SystemConfigurationBodyDTO,
	SystemConfigurationDTO,
	UpdateBusinessPremisesElsterConfigRequestParams,
	UpdateBusinessPremisesRequestParams,
	UpdateBusinessPremisesSchedulingConfigRequestParams,
	UpdateBusinessPremisesStatusRequestParams,
	UpdateBusinessPremisesSystemConfigRequestParams
}                             from '@openApiGenerated/management-api';
import { Address }            from '@shared/components/address/models/address.model';
import { LoggerService }      from '@shared/services/logger.service';
import { Observable }         from 'rxjs';


@Injectable( {
	providedIn: 'root'
} )
export class BusinessPremisesApiService extends AbstractApiService {

	constructor(
		private readonly adminBusinessPremisesApiClient: AdminBusinessPremisesApiClient,
		private readonly businessPremisesApiClient: BusinessPremisesApiClient,
		protected http: HttpClient,
		protected logger: LoggerService,
		private readonly mapperService: MapperService,
		private readonly sanitize: SanitizerService,
		private readonly transition: TransitionService
	) {
		super();
		this.http = http;
	}


	// SANITIZER ######################################
	private sanitizeApiResponse( entity: BusinessPremises, context = this ): void {
		if ( entity instanceof BusinessPremises ) {
			entity.addressData = context.sanitize.address( entity.addressData, ModelTypeEnum.TMO ) as Address;
			entity.createdDate = context.sanitize.date<Date>( entity.createdDate, ModelTypeEnum.TMO, Date );
			entity.deactivationDate = context.sanitize.date<Date>( entity.deactivationDate, ModelTypeEnum.TMO, Date );
			entity.lastModifiedDate = context.sanitize.date<Date>( entity.lastModifiedDate, ModelTypeEnum.TMO, Date );
			entity.lastSuccessRegistrationProtocolPreviewDate = context.sanitize.date<Date>( entity.lastSuccessRegistrationProtocolPreviewDate, ModelTypeEnum.TMO, Date );

			if ( entity.errors.invalidEntities ) {
				entity.errors.invalidEntities =
					context.mapperService.errors.invalidEntitiesDtoToTmo( entity.errors.invalidEntities as unknown as InvalidEntitiesDTO[] );
			}
		}
	}

	private sanitizeForApiRequest( dto: BusinessPremisesBodyDTO | BusinessPremisesUpdateBodyDTO, context = this ): void {
		if ( dto instanceof ClsBusinessPremisesBodyDTO || dto instanceof ClsBusinessPremisesUpdateBodyDTO ) {
			dto.addressData = context.sanitize.address( dto.addressData, ModelTypeEnum.DTO ) as AddressDataDTO;
		}
	}

	private sanitizeSchedulingConfigApiResponse( entity: SchedulingConfig, context = this ): void {
		context.sanitize.schedulingConfig( entity );
	}

	private sanitizeElsterConfigApiResponse( entity: ElsterConfig, context = this ): void {
		context.sanitize.elsterConfig( entity );
	}

	private sanitizeSystemConfigApiResponse( entity: SystemConfig, context = this ): void {
		context.sanitize.systemConfig( entity );
	}


	// LIST TYPE REQUESTS #############################
	public list( throwError = false ): Observable<RequestCompleteListState<BusinessPremises>> {

		this.logger.debug( 'pre-list call' );

		const requestParam: GetBusinessPremisesByCriteriaRequestParams = {
			businessPremisesIds: null,
			filter: null,
			hasErrors: null,
			hasLastSuccessRegistrationProtocolPreviewDate: null,
			hasValidationResults: null,
			organizationId: null,
			pageNumber: null,
			pageSize: null,
			search: null,
			sort: 'lastModifiedDate,desc',
			status: null
		};
		const req$ = this.businessPremisesApiClient.getBusinessPremisesByCriteria( requestParam );
		return this.transition.handleListRequestResponse<BusinessPremisesDTO, BusinessPremises>(
			req$,
			BusinessPremises,
			this,
			this.sanitizeApiResponse,
			null,
			null,
			throwError
		);
	}

	public search(
		params: IBusinessPremisesHttpParams,
		httpContextConfig: IHttpContextConfig,
		throwError = false
	): Observable<RequestCompleteListState<BusinessPremises>> {
		this.logger.debug( 'pre-search call: ', params );

		const requestParam: GetBusinessPremisesByCriteriaRequestParams = {
			businessPremisesIds: this.arrayUtils.checkForSingleValueArgAndReturnAsArray<string>( params?.businessPremisesIds ),
			filter: params?.filter,
			hasErrors: params?.hasErrors,
			hasValidationResults: params?.hasValidationResults,
			hasLastSuccessRegistrationProtocolPreviewDate: this.mapperService.taxation.isNotYetTaxReportedToHasLastSuccessRPPreviewDate( params?.isNotYetTaxReported ),
			organizationId: this.arrayUtils.checkForArrayArgAndReturnAsSingleValue<string>( params?.organizationIds ),
			pageNumber: params?.paging?.pageNumber,
			pageSize: params?.paging?.pageSize,
			search: this.arrayUtils.checkForSingleValueArgAndReturnAsArray( params?.search ),
			sort: params?.sort,
			status:
				this.arrayUtils.checkForSingleValueArgAndReturnAsArray<BusinessPremisesStatusEnumDTO>(
					params?.status as unknown as BusinessPremisesStatusEnumDTO[]
				)
		};
		const req$ =
			this.businessPremisesApiClient.getBusinessPremisesByCriteria(
				requestParam,
				'body',
				false,
				this.buildHttpContext( httpContextConfig )
			);

		return this.transition.handleListRequestResponse<BusinessPremisesDTO, BusinessPremises>(
			req$,
			BusinessPremises,
			this,
			this.sanitizeApiResponse,
			null,
			null,
			throwError
		);
	}

	public listByOrganizationId( parentOrgId: string, params?: IBusinessPremisesHttpParams ): Observable<RequestCompleteListState<BusinessPremises>> {
		this.logger.debug( 'pre-listByOrganizationId call: ', parentOrgId, params );

		const requestParam: GetBusinessPremisesByCriteriaRequestParams = {
			hasErrors: params?.hasErrors,
			hasLastSuccessRegistrationProtocolPreviewDate: this.mapperService.taxation.isNotYetTaxReportedToHasLastSuccessRPPreviewDate( params?.isNotYetTaxReported ),
			hasValidationResults: params?.hasValidationResults,
			organizationId: parentOrgId,
			pageNumber: params?.paging?.pageNumber,
			pageSize: params?.paging?.pageSize,
			sort: params?.sort,
			status:
				this.arrayUtils.checkForSingleValueArgAndReturnAsArray<BusinessPremisesStatusEnumDTO>(
					params?.status as unknown as BusinessPremisesStatusEnumDTO[]
				),
		};
		const req$ = this.businessPremisesApiClient.getBusinessPremisesByCriteria( requestParam );
		return this.transition.handleListRequestResponse<BusinessPremisesDTO, BusinessPremises>(
			req$, BusinessPremises, this, this.sanitizeApiResponse
		);
	}


	// ENTITY TYPE REQUESTS ###########################
	public read(
		businessPremisesId: string,
		options: {
			httpContextConfig?: IHttpContextConfig,
			throwError?: boolean,
			withFullResponse?: boolean,
		}
	): Observable<RequestCompleteEntityState<BusinessPremises>> {
		this.logger.debug( 'pre-read call', 'businessPremisesId', businessPremisesId, 'options', JSON.stringify( options ) );

		const { httpContextConfig, throwError = false, withFullResponse = true } = options;
		const requestParam: GetBusinessPremisesRequestParams = {
			businessPremisesId
		};

		const req$ = this.businessPremisesApiClient.getBusinessPremises(
			requestParam,
			withFullResponse ? 'response' : undefined,
			false,
			this.buildHttpContext( httpContextConfig )
		);

		return this.transition.handleEntityRequestResponse<BusinessPremisesDTO | HttpResponse<BusinessPremisesDTO>, BusinessPremises>(
			req$,
			RequestActionEnum.READ,
			BusinessPremises,
			this,
			this.sanitizeApiResponse,
			null,
			null,
			null,
			throwError
		);
	}

	public create( bp: BusinessPremises, throwError = false ): Observable<RequestCompleteEntityState<BusinessPremises>> {
		this.logger.debug( 'pre-create call: ', { ...bp } );

		const businessPremisesBodyDTO = this.transition.prepareEntityRequest<BusinessPremises, BusinessPremisesBodyDTO>(
			bp, ClsBusinessPremisesBodyDTO, this, this.sanitizeForApiRequest
		);
		const requestParam: CreateBusinessPremisesForOrganizationRequestParams = {
			organizationId: bp?.parentOrgId,
			businessPremisesBodyDTO
		};
		const req$ = this.businessPremisesApiClient.createBusinessPremisesForOrganization( requestParam );
		return this.transition.handleEntityRequestResponse<BusinessPremisesDTO, BusinessPremises>(
			req$,
			RequestActionEnum.CREATE,
			BusinessPremises,
			this,
			this.sanitizeApiResponse,
			null,
			null,
			null,
			throwError
		);
	}

	public update( businessPremises: BusinessPremises, throwError = false ): Observable<RequestCompleteEntityState<BusinessPremises>> {
		this.logger.debug( 'pre-update call: ', { ...businessPremises } );

		const businessPremisesUpdateBodyDTO = this.transition.prepareEntityRequest<BusinessPremises, BusinessPremisesUpdateBodyDTO>(
			businessPremises, ClsBusinessPremisesUpdateBodyDTO, this, this.sanitizeForApiRequest
		);
		const requestParam: UpdateBusinessPremisesRequestParams = {
			businessPremisesId: businessPremises.id,
			ifMatch: businessPremises.etag,
			businessPremisesUpdateBodyDTO
		};
		const req$ = this.businessPremisesApiClient.updateBusinessPremises( requestParam );
		return this.transition.handleEntityRequestResponse<BusinessPremisesDTO, BusinessPremises>(
			req$,
			RequestActionEnum.UPDATE,
			BusinessPremises,
			this,
			this.sanitizeApiResponse,
			null,
			null,
			null,
			throwError
		);
	}

	public changeStatus( businessPremises: BusinessPremises, throwError = false ) {

		const businessPremisesStatusDTO = this.transition.prepareEntityRequest<BusinessPremises, BusinessPremisesStatusDTO>(
			businessPremises, ClsBusinessPremisesStatusDTO, this, this.sanitizeForApiRequest
		);

		const requestParam: UpdateBusinessPremisesStatusRequestParams = {
			businessPremisesId: businessPremises.id,
			ifMatch: businessPremises.etag,
			businessPremisesStatusDTO
		};

		const req$ = this.businessPremisesApiClient.updateBusinessPremisesStatus( requestParam );
		return this.transition.handleEntityRequestResponse<BusinessPremisesDTO | HttpResponse<BusinessPremisesDTO>, BusinessPremises>(
			req$,
			RequestActionEnum.PATCH,
			BusinessPremises,
			this,
			this.sanitizeApiResponse,
			null,
			null,
			null,
			throwError
		);
	}

	public delete( businessPremisesId: string, throwError = false ): Observable<RequestCompleteEntityState<BusinessPremises>> {
		this.logger.debug( 'pre-delete call: ', businessPremisesId );

		const requestParam: DeleteBusinessPremisesRequestParams = {
			businessPremisesId
		};
		const req$ = this.businessPremisesApiClient.deleteBusinessPremises( requestParam, 'response' );
		return this.transition.handleEntityRequestResponse<HttpResponse<any>, BusinessPremises>(
			req$,
			RequestActionEnum.DELETE,
			BusinessPremises,
			this,
			null,
			null,
			null,
			null,
			throwError
		);
	}

	// Scheduling Config
	public readSchedulingConfig( businessPremisesId: string, withFullResponse: boolean ): Observable<RequestCompleteEntityState<SchedulingConfig>> {
		this.logger.debug( 'pre-readBusinessPremisesConfig call: ', businessPremisesId );
		const requestParam: GetBusinessPremisesSchedulingConfigRequestParams = {
			businessPremisesId
		};
		const req$ = this.businessPremisesApiClient.getBusinessPremisesSchedulingConfig( requestParam, withFullResponse ? 'response' : undefined );
		return this.transition.handleEntityRequestResponse<SchedulingConfigurationDTO | HttpResponse<SchedulingConfigurationDTO>, SchedulingConfig>(
			req$,
			RequestActionEnum.READ,
			SchedulingConfig,
			this,
			this.sanitizeSchedulingConfigApiResponse
		);
	}

	public createSchedulingConfig( businessPremisesId: string, configuration: SchedulingConfig ): Observable<RequestCompleteEntityState<SchedulingConfig>> {
		this.logger.debug( 'pre-createBusinessPremisesConfig call: ', businessPremisesId, configuration );

		const schedulingConfigurationDTO = this.transition.prepareEntityRequest<SchedulingConfig, SchedulingConfigurationDTO>(
			configuration, ClsSchedulingConfigurationDTO, this
		);

		const requestParam: CreateBusinessPremisesSchedulingConfigRequestParams = {
			businessPremisesId,
			schedulingConfigurationDTO
		};

		const req$ = this.businessPremisesApiClient.createBusinessPremisesSchedulingConfig( requestParam );
		return this.transition.handleEntityRequestResponse<SchedulingConfigurationDTO | HttpResponse<SchedulingConfigurationDTO>, SchedulingConfig>(
			req$,
			RequestActionEnum.CREATE,
			SchedulingConfig,
			this,
			this.sanitizeSchedulingConfigApiResponse
		);
	}

	public updateSchedulingConfig( businessPremisesId: string, configuration: SchedulingConfig ): Observable<RequestCompleteEntityState<SchedulingConfig>> {
		this.logger.debug( 'pre-updateBusinessPremisesConfig call: ', businessPremisesId, configuration );
		const schedulingConfigurationDTO = this.transition.prepareEntityRequest<SchedulingConfig, SchedulingConfigurationDTO>(
			configuration, ClsSchedulingConfigurationDTO, this
		);

		const requestParam: UpdateBusinessPremisesSchedulingConfigRequestParams = {
			businessPremisesId,
			ifMatch: configuration?.etag ?? '-1',
			schedulingConfigurationDTO
		};

		const req$ = this.businessPremisesApiClient.updateBusinessPremisesSchedulingConfig( requestParam );
		return this.transition.handleEntityRequestResponse<SchedulingConfigurationDTO | HttpResponse<SchedulingConfigurationDTO>, SchedulingConfig>(
			req$,
			RequestActionEnum.UPDATE,
			SchedulingConfig,
			this,
			this.sanitizeSchedulingConfigApiResponse
		);
	}

	public deleteSchedulingConfig( businessPremisesId: string ): Observable<RequestCompleteEntityState<SchedulingConfig>> {
		this.logger.debug( 'pre-deleteSchedulingConfig call: ', businessPremisesId );
		const requestParam: DeleteBusinessPremisesSchedulingConfigRequestParams = {
			businessPremisesId
		};

		const req$ = this.businessPremisesApiClient.deleteBusinessPremisesSchedulingConfig( requestParam );
		return this.transition.handleEntityRequestResponse<HttpResponse<any>, SchedulingConfig>(
			req$,
			RequestActionEnum.DELETE,
			SchedulingConfig,
			this,
			null,
			null,
			null,
			null
		);
	}


	// System Config
	public readSystemConfig(
		options: {
			businessPremisesId: string,
			withFullResponse?: boolean
		}
	): Observable<RequestCompleteEntityState<SystemConfig>> {
		const {
			businessPremisesId,
			withFullResponse = true
		} = options;


		this.logger.debug( 'pre-readBusinessPremisesSystemConfig call: ', businessPremisesId );
		const requestParam: GetBusinessPremisesSystemConfigRequestParams = {
			businessPremisesId
		};
		const req$ = this.businessPremisesApiClient.getBusinessPremisesSystemConfig( requestParam, withFullResponse ? 'response' : undefined );
		return this.transition.handleEntityRequestResponse<SystemConfigurationDTO | HttpResponse<SystemConfigurationDTO>, SystemConfig>(
			req$,
			RequestActionEnum.READ,
			SystemConfig,
			this,
			this.sanitizeSystemConfigApiResponse
		);
	}

	public createSystemConfig(
		options: {
			businessPremisesId: string,
			configuration: SystemConfig,
			useAdminApi?: boolean,
			withFullResponse?: boolean
		}
	): Observable<RequestCompleteEntityState<SystemConfig>> {
		const {
			businessPremisesId,
			configuration,
			useAdminApi = false,
			withFullResponse = true
		} = options;

		this.logger.debug( 'pre-createBusinessPremisesConfig call: ', businessPremisesId, configuration );

		const systemConfigurationBodyDTO = this.transition.prepareEntityRequest<SystemConfig, SystemConfigurationBodyDTO>(
			configuration, ClsSystemConfigurationBodyDTO, this
		);

		const requestParam: CreateBusinessPremisesSystemConfigRequestParams = {
			businessPremisesId,
			systemConfigurationBodyDTO
		};

		const adminRequestParam: AdminCreateBusinessPremisesSystemConfigRequestParams = {
			businessPremisesId,
			adminSystemConfigurationBodyDTO: systemConfigurationBodyDTO
		};

		const responseMode = withFullResponse ? 'response' : undefined;

		const req$ = useAdminApi
								 ? this.adminBusinessPremisesApiClient.createBusinessPremisesSystemConfig( adminRequestParam, responseMode )
								 : this.businessPremisesApiClient.createBusinessPremisesSystemConfig( requestParam, responseMode );


		return this.transition.handleEntityRequestResponse<SystemConfigurationDTO | HttpResponse<SystemConfigurationDTO>, SystemConfig>(
			req$,
			RequestActionEnum.CREATE,
			SystemConfig,
			this,
			this.sanitizeSystemConfigApiResponse
		);
	}

	public updateSystemConfig(
		options: {
			businessPremisesId: string,
			configuration: SystemConfig,
			useAdminApi?: boolean,
			withFullResponse?: boolean
		}
	): Observable<RequestCompleteEntityState<SystemConfig>> {
		const {
			businessPremisesId,
			configuration,
			useAdminApi = false,
			withFullResponse = true
		} = options;

		this.logger.debug( 'pre-updateBusinessPremisesSystemConfig call: ', businessPremisesId, configuration );
		const systemConfigurationBodyDTO = this.transition.prepareEntityRequest<SystemConfig, SystemConfigurationBodyDTO>(
			configuration, ClsSystemConfigurationBodyDTO, this
		);

		const requestParam: UpdateBusinessPremisesSystemConfigRequestParams = {
			businessPremisesId,
			ifMatch: configuration?.etag ?? '-1',
			systemConfigurationBodyDTO
		};


		const adminRequestParam: AdminUpdateBusinessPremisesSystemConfigRequestParams = {
			businessPremisesId,
			ifMatch: configuration?.etag ?? '-1',
			adminSystemConfigurationBodyDTO: systemConfigurationBodyDTO
		};

		const responseMode = withFullResponse ? 'response' : undefined;

		const req$ = useAdminApi
								 ? this.adminBusinessPremisesApiClient.updateBusinessPremisesSystemConfig( adminRequestParam, responseMode )
								 : this.businessPremisesApiClient.updateBusinessPremisesSystemConfig( requestParam, responseMode );

		return this.transition.handleEntityRequestResponse<SystemConfigurationDTO | HttpResponse<SystemConfigurationDTO>, SystemConfig>(
			req$,
			RequestActionEnum.UPDATE,
			SystemConfig,
			this,
			this.sanitizeSystemConfigApiResponse
		);
	}

	public deleteSystemConfig( options: { businessPremisesId: string } ): Observable<RequestCompleteEntityState<SystemConfig>> {
		const {
			businessPremisesId
		} = options;

		this.logger.debug( 'pre-deleteSystemConfig call: ', businessPremisesId );
		const requestParam: DeleteBusinessPremisesSystemConfigRequestParams = {
			businessPremisesId
		};

		const req$ = this.adminBusinessPremisesApiClient.deleteBusinessPremisesSystemConfig( requestParam );
		return this.transition.handleEntityRequestResponse<HttpResponse<any>, SystemConfig>(
			req$,
			RequestActionEnum.DELETE,
			SystemConfig,
			this
		);
	}

	// Elster Config
	public readElsterConfig(
		options: {
			businessPremisesId: string,
			useAdminApi?: boolean
			withFullResponse?: boolean
		}
	): Observable<RequestCompleteEntityState<ElsterConfig>> {
		const {
			businessPremisesId,
			withFullResponse = true,
			useAdminApi = false
		} = options;

		const observe = withFullResponse ? 'response' : undefined;

		this.logger.debug( 'pre-readElsterConfig call: ', businessPremisesId );

		const requestParam: GetBusinessPremisesElsterConfigRequestParams | AdminGetBusinessPremisesElsterConfigRequestParams = {
			businessPremisesId
		};

		const req$: Observable<ElsterConfigurationDTO | HttpResponse<ElsterConfigurationDTO>> =
			useAdminApi
			? this.adminBusinessPremisesApiClient.getBusinessPremisesElsterConfig( requestParam, observe )
			: this.businessPremisesApiClient.getBusinessPremisesElsterConfig( requestParam, observe );


		return this.transition.handleEntityRequestResponse<ElsterConfigurationDTO | HttpResponse<ElsterConfigurationDTO>, ElsterConfig>(
			req$,
			RequestActionEnum.READ,
			ElsterConfig,
			this,
			this.sanitizeElsterConfigApiResponse
		);
	}

	public createElsterConfig(
		options: {
			businessPremisesId: string,
			configuration: ElsterConfig,
			useAdminApi?: boolean,
			withFullResponse?: boolean
		}
	): Observable<RequestCompleteEntityState<ElsterConfig>> {
		const {
			businessPremisesId,
			configuration,
			useAdminApi = false,
			withFullResponse = true
		} = options;

		const observe = withFullResponse ? 'response' : undefined;

		this.logger.debug( 'pre-createElsterConfig call: ', businessPremisesId, configuration );


		const requestParam: CreateBusinessPremisesElsterConfigRequestParams | AdminCreateBusinessPremisesElsterConfigRequestParams = {
			businessPremisesId,
			elsterConfigurationDTO: {
				ericEnabled: configuration?.taxationReportingEnabled,
				type: configuration?.type as unknown as ConfigTypeDTO
			}
		};

		const req$: Observable<ElsterConfigurationDTO | HttpResponse<ElsterConfigurationDTO>> =
			useAdminApi
			? this.adminBusinessPremisesApiClient.createBusinessPremisesElsterConfig( requestParam, observe )
			: this.businessPremisesApiClient.createBusinessPremisesElsterConfig( requestParam, observe );


		return this.transition.handleEntityRequestResponse<ElsterConfigurationDTO | HttpResponse<ElsterConfigurationDTO>, ElsterConfig>(
			req$,
			RequestActionEnum.CREATE,
			ElsterConfig,
			this,
			this.sanitizeElsterConfigApiResponse
		);
	}

	public updateElsterConfig(
		options: {
			businessPremisesId: string,
			configuration: ElsterConfig,
			useAdminApi?: boolean,
			withFullResponse?: boolean
		}
	): Observable<RequestCompleteEntityState<ElsterConfig>> {
		const {
			businessPremisesId,
			configuration,
			useAdminApi = false,
			withFullResponse = true
		} = options;

		const observe = withFullResponse ? 'response' : undefined;

		this.logger.debug( 'pre-updateElsterConfig call: ', businessPremisesId, configuration );

		const requestParam: AdminUpdateBusinessPremisesElsterConfigRequestParams | UpdateBusinessPremisesElsterConfigRequestParams = {
			businessPremisesId,
			ifMatch: configuration?.etag ?? '-1',
			elsterConfigurationDTO: {
				ericEnabled: configuration?.taxationReportingEnabled,
				type: configuration?.type as unknown as ConfigTypeDTO
			}
		};

		const req$: Observable<ElsterConfigurationDTO | HttpResponse<ElsterConfigurationDTO>> =
			useAdminApi
			? this.adminBusinessPremisesApiClient.updateBusinessPremisesElsterConfig( requestParam, observe )
			: this.businessPremisesApiClient.updateBusinessPremisesElsterConfig( requestParam, observe );

		return this.transition.handleEntityRequestResponse<ElsterConfigurationDTO | HttpResponse<ElsterConfigurationDTO>, ElsterConfig>(
			req$,
			RequestActionEnum.UPDATE,
			ElsterConfig,
			this,
			this.sanitizeElsterConfigApiResponse
		);
	}

	public deleteElsterConfig(
		options: {
			businessPremisesId: string,
			useAdminApi?: boolean
		}
	): Observable<RequestCompleteEntityState<ElsterConfig>> {
		const {
			businessPremisesId,
			useAdminApi = false
		} = options;

		this.logger.debug( 'pre-deleteElsterConfig call: ', businessPremisesId );

		const requestParam: AdminDeleteBusinessPremisesElsterConfigRequestParams | DeleteBusinessPremisesElsterConfigRequestParams = {
			businessPremisesId
		};

		const req$: Observable<any | HttpResponse<any>> =
			useAdminApi
			? this.adminBusinessPremisesApiClient.deleteBusinessPremisesElsterConfig( requestParam )
			: this.businessPremisesApiClient.deleteBusinessPremisesElsterConfig( requestParam );

		return this.transition.handleEntityRequestResponse<HttpResponse<any>, ElsterConfig>(
			req$,
			RequestActionEnum.DELETE,
			ElsterConfig,
			this
		);
	}
}
